import { EventFilter } from '@fullstory/proto/fs/api/segment';
import * as vars from '@fullstory/proto/fs/api/vars';
import { CompletionGroup, FieldSuggestRequest } from '@fullstory/proto/fs/services/alexa';
import { AppDispatch, SearchAsyncActions } from '@fullstory/redux';
import { assertExhaustive } from '@fullstory/typeutils';
import React, { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

interface Property {
  id: string;
  name: string;
  propertyType: vars.Type;
  propertyScope: vars.Scope;
}

interface usePropertySampleValuesProps {
  customFilter?: EventFilter;
}

export const usePropertySampleValues = ({ customFilter }: usePropertySampleValuesProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const [_, setSampleValuesVersion] = React.useState(0); // for ensuring re-render when sample values are fetched
  const sampleValuesRef = useRef(new Map<string, string[] | undefined>());

  const fetchSampleValues = useCallback(
    (properties: Property[]): string[] => {
      // additionally, we want to fetch some sample values from auto suggest for any applicable properties
      const sampleValuePromises = new Map<string, Promise<CompletionGroup[]>>();
      properties.forEach(prop => {
        if (sampleValuesRef.current.has(prop.id)) {
          // already fetched (either in progress or resolved)
          return;
        }
        const req = makeReq(prop, customFilter);
        if (req) {
          sampleValuePromises.set(prop.id, dispatch(SearchAsyncActions.autocomplete(req, '')));
        }
      });
      // mark each fetching prop as loading (the rest aren't applicable for sample values)
      const sampleValuesWithPending = new Map(sampleValuesRef.current);
      sampleValuePromises.forEach((_, id) => {
        sampleValuesWithPending.set(id, undefined);
      });
      sampleValuesRef.current = sampleValuesWithPending;

      const handlePromises = async () => {
        const newResults = new Map<string, string[]>();
        for (const [id, promise] of sampleValuePromises) {
          const completions = await promise;
          newResults.set(
            id,
            completions.flatMap(group => group.completions.map(c => c.text)),
          );
        }
        const sampleValuesWithResult = new Map(sampleValuesRef.current);
        newResults.forEach((values, id) => {
          sampleValuesWithResult.set(id, values);
        });
        sampleValuesRef.current = sampleValuesWithResult;
        setSampleValuesVersion(version => version + 1);
      };
      handlePromises();

      // return ids of properties that have or will have valid sample values
      return properties.filter(prop => sampleValuesRef.current.has(prop.id)).map(prop => prop.id);
    },
    [customFilter],
  );

  return { sampleValues: sampleValuesRef.current, fetchSampleValues };
};

const makeReq = (
  prop: Property,
  customFilter: EventFilter = { custom: { any: {} } },
): Partial<Pick<FieldSuggestRequest, 'dependentEventFilter'>> | undefined => {
  switch (prop.propertyScope) {
    case vars.Scope.evt: {
      switch (prop.propertyType) {
        case vars.Type.str: {
          return {
            dependentEventFilter: {
              dependentEventFilter: {
                customEventPropertyString: { equals: { name: prop.name, value: [] } },
              },
              eventFilter: customFilter,
            },
          };
        }
        case vars.Type.strs: {
          return {
            dependentEventFilter: {
              dependentEventFilter: {
                customEventPropertyStrings: { equals: { name: prop.name, value: [] } },
              },
              eventFilter: customFilter,
            },
          };
        }
      }
      return undefined;
    }
    case vars.Scope.elem: {
      switch (prop.propertyType) {
        case vars.Type.str: {
          return {
            dependentEventFilter: {
              dependentEventFilter: {
                elemVarString: { equals: { name: prop.name, value: [] } },
              },
              eventFilter: null,
            },
          };
        }
        case vars.Type.strs: {
          return {
            dependentEventFilter: {
              dependentEventFilter: {
                elemVarStrings: { equals: { name: prop.name, value: [] } },
              },
              eventFilter: null,
            },
          };
        }
      }
      return undefined;
    }
    case vars.Scope.page: {
      switch (prop.propertyType) {
        case vars.Type.str: {
          return {
            dependentEventFilter: {
              dependentEventFilter: {
                pageVarString: { equals: { name: prop.name, value: [] } },
              },
              eventFilter: null,
            },
          };
        }
        case vars.Type.strs: {
          return {
            dependentEventFilter: {
              dependentEventFilter: {
                pageVarStrings: { equals: { name: prop.name, value: [] } },
              },
              eventFilter: null,
            },
          };
        }
      }
      return undefined;
    }
    case vars.Scope.user: {
      switch (prop.propertyType) {
        case vars.Type.str: {
          return {
            dependentEventFilter: {
              dependentEventFilter: {
                userVarString: { equals: { name: prop.name, value: [] } },
              },
              eventFilter: null,
            },
          };
        }
        case vars.Type.strs: {
          return {
            dependentEventFilter: {
              dependentEventFilter: {
                userVarStrings: { equals: { name: prop.name, value: [] } },
              },
              eventFilter: null,
            },
          };
        }
      }
      return undefined;
    }
    case vars.Scope.fsevt:
      return undefined;
    case vars.Scope.INVALID_SCOPE:
    // fallthrough
    case vars.Scope.document:
      return undefined;
    default:
      assertExhaustive(prop.propertyScope);
  }
};
